import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserContext } from '../../../components/auth/auth-context';
import { isSameDay } from "date-fns";
import { SIGNUP_USER } from '../../../graphql/mutations';
import useLoadReduxStoreBookings from '../../../redux/reducers/loadReduxStoreBooking';
import { useSelectBusinesses, useSelectedBusiness } from '../../../redux/store';
import { BusinessQueryNames, GET_GOOGLE_CALENDAR_EVENTS } from '../../../graphql/queries';
import useLoadReduxStoreData from '../../../redux/reducers/loadReduxStoreData';
import { useAuthStatus } from '../../../util/useAuthStatus';

const CalendarContext = createContext();

export const CalendarProvider = ({ children, myBookingTypes }) => {
  const { isLoggedIn } = useAuthStatus();

    const { refetch: reloadBusinesses } = useLoadReduxStoreData(
      BusinessQueryNames.GET_MY_BUSINESSES,
      { enabled: isLoggedIn }
    );
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;
  const [signUpUser] = useMutation(SIGNUP_USER);
  const { businessUrl } = useParams();
  const location = useLocation();
  const isMySchedule = location.pathname.includes('myschedule');

  const [businessRegistered, setBusinessRegistered] = useState(false);
  const business = useSelectBusinesses();
  const selectedBusiness = useSelectedBusiness();
  const businessBasic = selectedBusiness?.businessBasic;
  const navigate = useNavigate();
  const businessStaffs = selectedBusiness?.businessStaffs;
  // State for merged bookings.
  const [bookingDatas, setBookingDatas] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const date = new Date(currentMonth);
  const fullYear = Number(date.getFullYear());

  const staffRecord = selectedBusiness?.businessStaffs?.find(
    (staff) => Number(staff.id) === providerUser.staffId
  );

  // Load base bookings from your store.
  const { bookings: bookingsFromStore, refetch: refetchBookings } =
    useLoadReduxStoreBookings(Number(selectedBusiness?.businessBasic?.id), providerUser.email, fullYear, {
      fetchPolicy: 'network-only',
    });

  // Query for Google events.
  const { loading: googleEventsLoading, data: googleEventsData, error: googleEventsError } = useQuery(GET_GOOGLE_CALENDAR_EVENTS, {
    variables: { staffId: providerUser?.staffId, currentMonth },
    skip: !staffRecord?.showGoogleEvents || !providerUser?.staffId,
  });

  // Register business if needed.
  useEffect(() => {
    if (!businessUrl) {
      if (isMySchedule) {
        return;
      } else if (providerUser.defaultBusinessId && !businessRegistered) {
        registerBusiness();
        setBusinessRegistered(true);
      } else if (providerUser && (!businessBasic?.address || !businessBasic?.cityId)) {
        navigate('/biz/business');
      }
    }
  }, [businessBasic, businessRegistered, providerUser.defaultBusinessId, navigate]);

  // Refetch bookings once on mount.
  useEffect(() => {
    refetchBookings();
  }, []);

  const registerBusiness = () => {
    signUpUser({
      variables: { email: providerUser.email },
    })
      .then((result) => {
        if (result?.data?.login?.token) {
          localStorage.setItem("token", result.data.login.token);
          userContext.login(result.data.login.token);
        }
      })
      .catch((error) => {
        console.error("Error registering business:", error);
      });
  };

  // Memoize Google events array.
  const googleEventsArray = useMemo(() => {
    return googleEventsData?.getGoogleCalendarEvents?.events || [];
  }, [googleEventsData]);

  // Ensure base bookings are an array.
  const baseBookings = useMemo(() => {
    return Array.isArray(bookingsFromStore) ? bookingsFromStore : [];
  }, [bookingsFromStore]);

  // Custom merge function to update bookingDatas.
  const updateBookingDatas = useCallback(() => {
    const mergedBookingData = [...baseBookings, ...googleEventsArray];
    setBookingDatas(mergedBookingData);
  }, [baseBookings, googleEventsArray]);

  // Update bookingDatas when base bookings or Google events change.
  useEffect(() => {
    updateBookingDatas();
  }, [updateBookingDatas]);

  // Reload businesses when 'business' changes.
  useEffect(() => {
    reloadBusinesses();
  }, [business, reloadBusinesses]);

  // Expose a refresh function to force an update after deletion.
  const refreshBookings = useCallback(async () => {
    // Force network fetch to get the latest data.
    await refetchBookings({ fetchPolicy: 'network-only' });
    updateBookingDatas();
  }, [refetchBookings, updateBookingDatas]);

  const getGridStyle = (date, verify, ind) => {
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    return {
      transition: "transform 0.3s ease-in-out",
      overflow: "hidden",
      userSelect: "none",
      padding: "10px",
      width: "140px",
      minWidth: isSelected ? "140px" : "",
      height: "140px",
      border: "1px solid var(--text-color-level3,rgba(26,26,26,0.1))",
      borderTop: ind < 7 ? "none" : undefined,
      borderLeft: ind < 7 ? "none" : undefined,
      background: verify ? "rgb(245,245,245)" : "white",
      zIndex: isSelected ? 2 : 1,
    };
  };

  const toggleDate = (date) => {
    setSelectedDate(date);
    setCurrentMonth(date);
  };

  const contextValue = {
    bookingDatas,
    updateBookingDatas,         // ✅ for internal recompute (merge)
    setBookingDatas,            // ✅ expose real setter for external updates 
    refreshBookings, // Expose the refresh function.
    currentMonth,
    setCurrentMonth,
    selectedDate,
    toggleDate,
    providerUser,
    setSelectedDate,
    getGridStyle,
    myBookingTypes,
    selectedBusiness,
    businessStaffs,
    refetchBookings,
    reloadBusinesses
  };

  if (googleEventsLoading) return "Loading...";
  if (googleEventsError) return "Error...";

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => useContext(CalendarContext);
